import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import Preloader from '../../components/custom/preLoader';
import axiosInstance from '../../components/content/axiosConfig';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';

const UpdateFisher = () => {
    const { id } = useParams();
    const [fisher, setFisher] = useState({
        FullName: '',
        Email: '',
        EndDate: '',
        Status: false,
        PackageID: '',
        LicensedPondCount: 1,
        ManagedByCompanyID: null,
        ManagedByCompanyName: null,
    });
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [detaching, setDetaching] = useState(false);
    const [assigning, setAssigning] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        const fetchData = async () => {
            try {
                const [fisherRes, packagesRes] = await Promise.all([
                    axiosInstance.get(`/api/fisher/GetFisher?id=${id}`),
                    axiosInstance.get('/api/packages/GetAll'),
                ]);
                const fetchedFisher = fisherRes.data;
                fetchedFisher.EndDate = fetchedFisher.EndDate
                    ? new Date(fetchedFisher.EndDate).toISOString().split('T')[0]
                    : '';
                fetchedFisher.PackageID = fetchedFisher.PackageID ?? '';
                fetchedFisher.LicensedPondCount = fetchedFisher.LicensedPondCount ?? 1;
                setFisher(fetchedFisher);

                const pkgRows = Array.isArray(packagesRes.data)
                    ? packagesRes.data
                    : packagesRes.data?.data ?? [];
                setPackages(pkgRows);
            } catch (error) {
                console.error('Error fetching fisher:', error);
            }
            setIsLoading(false);
        };
        fetchData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFisher({ ...fisher, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post(`/api/fisher/UpdateFisher?id=${id}`, fisher);
            showSuccessToast('Fisher updated successfully');
        } catch (error) {
            console.error('Error updating fisher:', error);
            showErrorToast('Error updating fisher');
        }
    };

    const handleDetach = async () => {
        if (detaching || !fisher.ManagedByCompanyID) return;
        const companyLabel = fisher.ManagedByCompanyName || 'their consultancy';
        const confirm = await Swal.fire({
            title: 'Remove from consultancy?',
            html: `<p class="mb-2">Remove <strong>${fisher.FullName || 'this account'}</strong> from <strong>${companyLabel}</strong>?</p>
              <p class="mb-0 text-muted small">They keep their ponds and data, and will manage their own subscription going forward. Company billing for this account will stop.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#0d9488',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove from consultancy',
            cancelButtonText: 'Cancel',
            focusCancel: true,
        });
        if (!confirm.isConfirmed) return;

        setDetaching(true);
        try {
            const res = await axiosInstance.post(`/api/fisher/DetachFromCompany?id=${id}`);
            showSuccessToast(
                res.data?.message ||
                    'Account removed from consultancy. They now manage their own subscription.'
            );
            setFisher((prev) => ({
                ...prev,
                ManagedByCompanyID: null,
                ManagedByCompanyName: null,
                isCompanyManagedBilling: false,
                ...(res.data?.account
                    ? {
                          EndDate: res.data.account.EndDate
                              ? new Date(res.data.account.EndDate).toISOString().split('T')[0]
                              : prev.EndDate,
                          PackageID: res.data.account.PackageID ?? prev.PackageID,
                      }
                    : {}),
            }));
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || 'Failed to remove account from consultancy'
            );
        } finally {
            setDetaching(false);
        }
    };

    const handleAssign = async () => {
        if (assigning || detaching || fisher.ManagedByCompanyID) return;
        setAssigning(true);
        try {
            const listRes = await axiosInstance.get('api/company/GetAll', {
                params: { page: 1, limit: 200 },
            });
            const companies = Array.isArray(listRes.data?.data)
                ? listRes.data.data
                : Array.isArray(listRes.data)
                  ? listRes.data
                  : [];
            const eligible = companies.filter(
                (c) => c.Status && !c.ConvertedAt && !c.ConvertedToFisherID
            );
            if (eligible.length === 0) {
                showErrorToast('No active companies available. Create one under Companies first.');
                return;
            }
            const options = {};
            eligible.forEach((c) => {
                options[String(c.id)] = `${c.Name} (${c.Email})`;
            });
            const pick = await Swal.fire({
                title: 'Assign to consultancy',
                html: '<p class="text-start small mb-2">Package and end date stay the same. The company handles renewals.</p>',
                input: 'select',
                inputOptions: options,
                inputPlaceholder: 'Choose company…',
                showCancelButton: true,
                confirmButtonText: 'Assign',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#0d9488',
                inputValidator: (value) => (!value ? 'Select a company' : null),
            });
            if (!pick.isConfirmed || !pick.value) return;

            const res = await axiosInstance.post(
                `api/company/${pick.value}/attach-account`,
                { fisherId: id }
            );
            showSuccessToast(res.data?.message || 'Account assigned to company');
            const company = eligible.find((c) => String(c.id) === String(pick.value));
            setFisher((prev) => ({
                ...prev,
                ManagedByCompanyID: Number(pick.value),
                ManagedByCompanyName: company?.Name || res.data?.company?.Name || null,
                ...(res.data?.account
                    ? {
                          EndDate: res.data.account.EndDate
                              ? new Date(res.data.account.EndDate).toISOString().split('T')[0]
                              : prev.EndDate,
                          PackageID: res.data.account.PackageID ?? prev.PackageID,
                      }
                    : {}),
            }));
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || 'Failed to assign account to company'
            );
        } finally {
            setAssigning(false);
        }
    };

    const isCompanyManaged = !!(fisher.ManagedByCompanyID || fisher.ManagedByCompanyName);

    return (
        isLoading ? <Preloader /> :
        <div>
            <form onSubmit={handleSubmit} className="fs-13px">
                <div className="d-flex align-items-center mb-3">
                    <div>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <Link to="/accounts">Accounts</Link>
                            </li>
                            <li className="breadcrumb-item Active">Update Fisher</li>
                        </ul>
                        <h1 className="page-header mb-0">Update Fisher</h1>
                    </div>
                    <div className="ms-auto d-flex gap-2">
                        {isCompanyManaged ? (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-rounded px-4 rounded-pill"
                                style={{ borderColor: '#99f6e4', color: '#0d9488' }}
                                disabled={detaching || assigning}
                                onClick={handleDetach}
                            >
                                {detaching ? 'Removing…' : 'Remove from consultancy'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-rounded px-4 rounded-pill"
                                style={{ borderColor: '#99f6e4', color: '#0d9488' }}
                                disabled={detaching || assigning}
                                onClick={handleAssign}
                            >
                                {assigning ? 'Assigning…' : 'Assign to consultancy'}
                            </button>
                        )}
                        <button type="submit" className="btn btn-success btn-rounded px-4 rounded-pill" disabled={detaching || assigning}>
                            Submit
                        </button>
                    </div>
                </div>
                <div className="card border-0">
                    <ul className="nav nav-tabs nav-tabs-v2 px-3">
                        <li className="nav-item me-2">
                            <Link className="nav-link px-2 active">General</Link>
                        </li>
                    </ul>
                    <div className="tab-content p-3">
                        <div className="tab-pane fade show active" id="allTab">
                            {isCompanyManaged ? (
                                <div
                                    className="alert mb-3 py-2 px-3"
                                    style={{
                                        background: '#f0fdfa',
                                        border: '1px solid #99f6e4',
                                        color: '#0f766e',
                                        borderRadius: 10,
                                    }}
                                >
                                    Managed by{' '}
                                    <strong>{fisher.ManagedByCompanyName || 'a Pond Management Company'}</strong>.
                                    Remove from consultancy to make this an independent owner account.
                                </div>
                            ) : null}
                            <div className="form-floating mb-15px">
                                <input
                                    type="text"
                                    className="form-control h-45px fs-13px"
                                    placeholder="Full Name"
                                    name="FullName"
                                    value={fisher.FullName}
                                    onChange={handleChange}
                                />
                                <label className="d-flex align-items-center fs-13px text-gray-600">
                                    Full Name
                                </label>
                            </div>
                            <div className="form-floating mb-15px">
                                <input
                                    type="text"
                                    className="form-control h-45px fs-13px"
                                    placeholder="Email"
                                    name="Email"
                                    readOnly
                                    value={fisher.Email}
                                />
                                <label className="d-flex align-items-center fs-13px text-gray-600">
                                    Email
                                </label>
                            </div>
                            <div className="form-floating mb-15px">
                                <input
                                    type="date"
                                    className="form-control h-45px fs-13px"
                                    placeholder="End Date"
                                    name="EndDate"
                                    value={fisher.EndDate}
                                    onChange={handleChange}
                                />
                                <label className="d-flex align-items-center fs-13px text-gray-600">
                                    Subscription end date
                                </label>
                            </div>
                            <div className="mb-15px">
                                <label className="form-label fs-13px text-gray-600">Package</label>
                                <select
                                    className="form-select h-45px fs-13px"
                                    name="PackageID"
                                    value={fisher.PackageID}
                                    onChange={handleChange}
                                >
                                    <option value="">None</option>
                                    {packages.map((pkg) => (
                                        <option key={pkg.id} value={pkg.id}>
                                            {pkg.Name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-floating mb-15px">
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control h-45px fs-13px"
                                    placeholder="Licensed Pond Count"
                                    name="LicensedPondCount"
                                    value={fisher.LicensedPondCount}
                                    onChange={handleChange}
                                />
                                <label className="d-flex align-items-center fs-13px text-gray-600">
                                    Licensed pond count
                                </label>
                            </div>
                            <div className="form-floating mb-15px">
                                <input
                                    type="checkbox"
                                    name="Status"
                                    checked={fisher.Status}
                                    onChange={() => setFisher({ ...fisher, Status: !fisher.Status })}
                                />
                                <label className="d-flex align-items-center fs-13px text-gray-600 ms-2">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default UpdateFisher;
